import { useCallback, useEffect, type RefObject } from 'react';
import { CircleAction } from '@domain/models';

/**
 * Drives arc fill/drain and dot orbit animations via strokeDashoffset transitions.
 * Shared by LungAnimation (ellipse) and ArcRing (circle) — geometry stays in each
 * component, animation logic lives here.
 */
export function useArcAnimation(
  arcRef: RefObject<SVGElement | null>,
  dotRef: RefObject<SVGElement | null>,
  circumference: number,
  phase: CircleAction,
  durationMs: number,
) {
  const animateArc = useCallback((totalMs: number) => {
    const arc = arcRef.current;
    if (!arc) return;
    arc.style.strokeDasharray = `${circumference} ${circumference}`;
    arc.style.strokeDashoffset = `${circumference}`;
    arc.style.transition = 'none';
    void (arc as SVGElement & { getBoundingClientRect(): DOMRect }).getBoundingClientRect();
    arc.style.transition = `stroke-dashoffset ${totalMs}ms cubic-bezier(0.37,0,0.63,1)`;
    arc.style.strokeDashoffset = '0';
  }, [arcRef, circumference]);

  const drainArc = useCallback((totalMs: number) => {
    const arc = arcRef.current;
    if (!arc) return;
    arc.style.strokeDasharray = `${circumference} ${circumference}`;
    arc.style.transition = 'none';
    arc.style.strokeDashoffset = '0';
    void (arc as SVGElement & { getBoundingClientRect(): DOMRect }).getBoundingClientRect();
    arc.style.transition = `stroke-dashoffset ${totalMs}ms cubic-bezier(0.37,0,0.63,1)`;
    arc.style.strokeDashoffset = `-${circumference}`;
  }, [arcRef, circumference]);

  const resetArc = useCallback(() => {
    const arc = arcRef.current;
    if (!arc) return;
    arc.style.transition = 'none';
    arc.style.strokeDashoffset = `${circumference}`;
  }, [arcRef, circumference]);

  const animateDot = useCallback((totalMs: number, eased: boolean) => {
    const dot = dotRef.current;
    if (!dot) return;
    dot.style.transition = 'none';
    dot.style.strokeDashoffset = '0';
    void (dot as SVGElement & { getBoundingClientRect(): DOMRect }).getBoundingClientRect();
    const timing = eased ? 'cubic-bezier(0.37,0,0.63,1)' : 'linear';
    dot.style.transition = `stroke-dashoffset ${totalMs}ms ${timing}`;
    dot.style.strokeDashoffset = `-${circumference}`;
  }, [dotRef, circumference]);

  const resetDot = useCallback(() => {
    const dot = dotRef.current;
    if (!dot) return;
    dot.style.transition = 'none';
    dot.style.strokeDashoffset = '0';
  }, [dotRef]);

  useEffect(() => {
    switch (phase) {
      case CircleAction.grow:
        animateArc(durationMs);
        animateDot(durationMs, true);
        break;
      case CircleAction.holdLarge:
        animateDot(durationMs, false);
        break;
      case CircleAction.shrink:
        drainArc(durationMs);
        animateDot(durationMs, true);
        break;
      case CircleAction.holdSmall:
        resetArc();
        animateDot(durationMs, false);
        break;
      default:
        resetArc();
        resetDot();
    }
  }, [phase, durationMs, animateArc, drainArc, resetArc, animateDot, resetDot]);
}
